import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, CheckCircle, Clock, IndianRupee, Eye, Trash2, Download, XCircle, User, AlertTriangle, MoreVertical } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import { salesOrdersAPI } from '../../services/part2-api';
import ColumnFilter from '../../components/shared/ColumnFilter';
import ListPageHeader from '../../components/shared/ListPageHeader';
import StaffFilterSelect from '../../components/shared/StaffFilterSelect';
import ConfirmModal from '../../components/modals/ConfirmModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getApiErrorMessage } from '../../utils/apiErrors';

const SalesOrdersPage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const [orders, setOrders] = useState([]);
  const [confirmModal, setConfirmModal] = useState(null);
  const [stats, setStats] = useState({ total: 0, draft: 0, confirmed: 0, total_value: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Role-based visibility
  const [isOwner, setIsOwner] = useState(true);
  const [staffList, setStaffList] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState(null);
  
  // Column filters
  const [columnFilters, setColumnFilters] = useState({
    order_number: '',
    customer_name: '',
    order_date: { min: '', max: '' },
    shipment_date: { min: '', max: '' },
    status: [],
    amount: { min: '', max: '' },
    staff_member: ''
  });

  useEffect(() => {
    const bid = businessId || activeBusiness?.id;
    if (bid) {
      fetchOrders(bid);
    }
  }, [businessId, activeBusiness, statusFilter, selectedStaffId]);

  const fetchOrders = async (bid) => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter.toUpperCase();
      if (searchQuery) params.search = searchQuery;
      // Staff filter for owner
      if (selectedStaffId) params.staff_id = selectedStaffId;

      const response = await salesOrdersAPI.list(params);
      const data = response.data;
      setOrders(data.orders || []);
      setStats(data.stats || { total: 0, draft: 0, confirmed: 0, total_value: 0 });
      // Set role-based data
      setIsOwner(data.is_owner !== false);
      setStaffList(data.staff_list || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load sales orders'));
    } finally {
      setLoading(false);
    }
  };

  // Apply all filters (search + column filters)
  const filteredOrders = orders.filter(order => {
    // Global search
    const matchesSearch = !searchQuery || 
      order.order_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.customer_name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Column filters
    const matchesOrderNo = !columnFilters.order_number || 
      order.order_number?.toLowerCase().includes(columnFilters.order_number.toLowerCase());
    
    const matchesCustomer = !columnFilters.customer_name || 
      order.customer_name?.toLowerCase().includes(columnFilters.customer_name.toLowerCase());
    
    const matchesOrderDate = (!columnFilters.order_date?.min || new Date(order.order_date) >= new Date(columnFilters.order_date.min)) &&
      (!columnFilters.order_date?.max || new Date(order.order_date) <= new Date(columnFilters.order_date.max));
    
    const matchesShipDate = (!columnFilters.shipment_date?.min || new Date(order.expected_shipment_date) >= new Date(columnFilters.shipment_date.min)) &&
      (!columnFilters.shipment_date?.max || new Date(order.expected_shipment_date) <= new Date(columnFilters.shipment_date.max));
    
    const matchesStatus = columnFilters.status.length === 0 || 
      columnFilters.status.includes(order.status);
    
    const matchesAmount = (!columnFilters.amount?.min || (order.grand_total || 0) >= parseFloat(columnFilters.amount.min)) &&
      (!columnFilters.amount?.max || (order.grand_total || 0) <= parseFloat(columnFilters.amount.max));
    
    // Staff member column filter (for owner view)
    const matchesStaffMember = !columnFilters.staff_member || 
      (order.created_by_staff_name || '').toLowerCase().includes(columnFilters.staff_member.toLowerCase());
    
    return matchesSearch && matchesOrderNo && matchesCustomer && matchesOrderDate && matchesShipDate && matchesStatus && matchesAmount && matchesStaffMember;
  });

  const handleAddOrder = () => {
    const bid = businessId || activeBusiness?.id;
    navigate(`/business/${bid}/sales-orders/new`);
  };

  const handleViewOrder = (order) => {
    const bid = businessId || activeBusiness?.id;
    navigate(`/business/${bid}/sales-orders/${order.id}`);
  };

  const handleConfirmOrder = (orderId, e) => {
    if (e) e.stopPropagation();
    setConfirmModal({
      title: 'Confirm Sales Order',
      description: 'Confirm this sales order? Inventory will be reserved.',
      confirmLabel: 'Confirm Order',
      confirmClass: 'bg-emerald-600 hover:bg-emerald-700',
      iconClass: 'text-emerald-600',
      iconBgClass: 'bg-emerald-100',
      icon: CheckCircle,
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          const bid = businessId || activeBusiness?.id;
          const response = await salesOrdersAPI.confirm(orderId);
          if (response.data) {
            fetchOrders(bid);
          }
        } catch (error) {
          console.error('Error confirming order:', error);
          toast.error(getApiErrorMessage(error, 'Failed to confirm order'));
        }
      }
    });
  };

  const handleCancelOrder = (orderId, e) => {
    if (e) e.stopPropagation();
    setConfirmModal({
      title: 'Cancel Sales Order',
      description: 'Order will be voided and any reserved inventory will be released. This cannot be undone.',
      confirmLabel: 'Cancel Order',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      iconClass: 'text-red-600',
      iconBgClass: 'bg-red-100',
      icon: XCircle,
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          const bid = businessId || activeBusiness?.id;
          const response = await salesOrdersAPI.cancel(orderId);
          if (response.data) {
            fetchOrders(bid);
          }
        } catch (error) {
          console.error('Error cancelling order:', error);
          toast.error(getApiErrorMessage(error, 'Failed to cancel order'));
        }
      }
    });
  };

  const handleDeleteOrder = (orderId, e) => {
    if (e) e.stopPropagation();
    setConfirmModal({
      title: 'Delete Draft Order',
      description: 'Delete this draft order? This cannot be undone.',
      confirmLabel: 'Delete',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      iconClass: 'text-red-600',
      iconBgClass: 'bg-red-100',
      icon: AlertTriangle,
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          const bid = businessId || activeBusiness?.id;
          await salesOrdersAPI.delete(orderId);
          fetchOrders(bid);
        } catch (error) {
          console.error('Error deleting order:', error);
          toast.error(getApiErrorMessage(error, 'Failed to delete order'));
        }
      }
    });
  };

  const handleDownloadPdf = async (orderId, e) => {
    e.stopPropagation();
    try {
      const response = await salesOrdersAPI.pdf(orderId);
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SalesOrder_${orderId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to download PDF'));
    }
  };

  const formatCompactCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount?.toFixed(0) || 0}`;
  };

  const getStatusBadge = (order) => {
    // The backend keeps `status = CONFIRMED` after convert-to-invoice (the
    // conversion is tracked via `converted_to_invoice` + `invoice_id`, not a
    // status transition). For the list UI, promote converted orders to an
    // "Invoiced" badge so users can distinguish them at a glance -- also
    // explains why Cancel is hidden in the actions menu for those rows.
    if (order.status === 'CONFIRMED' && order.converted_to_invoice) {
      return { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Invoiced' };
    }
    const badges = {
      DRAFT: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Draft' },
      CONFIRMED: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Confirmed' },
      CLOSED: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Closed' },
      CANCELLED: { bg: 'bg-red-100', text: 'text-red-700', label: 'Cancelled' }
    };
    return badges[order.status] || badges.DRAFT;
  };

  return (
    <>
    <div className="h-full flex flex-col" data-testid="sales-orders-page">
      {/* Premium Header (B2) - Module Header */}
      <ListPageHeader
        title="Sales Orders"
        stats={[
          { icon: FileText, label: 'Total', value: stats.total, color: 'slate' },
          { icon: Clock, label: 'Draft', value: stats.draft, color: 'amber' },
          { icon: CheckCircle, label: 'Confirmed', value: stats.confirmed, color: 'blue' },
          { icon: IndianRupee, label: 'Order Value', value: formatCompactCurrency(stats.total_value), color: 'emerald' },
        ]}
        ctaLabel="New Sales Order"
        onCtaClick={handleAddOrder}
        // Staff filter for owner view
        extraControls={isOwner && staffList.length > 0 ? (
          <StaffFilterSelect
            staffList={staffList}
            selectedStaffId={selectedStaffId}
            onChange={setSelectedStaffId}
          />
        ) : null}
      />

      {/* ════════════════════════════════════════════════════════════════════════
          TABLE AREA (T1) - With vertical gap from B2
          ════════════════════════════════════════════════════════════════════════ */}
      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
                <p className="text-[13px] text-slate-500 font-normal">Loading orders...</p>
              </div>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                <FileText className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-[15px] font-medium text-slate-600">
                {searchQuery ? 'No orders found' : 'No sales orders yet'}
              </p>
              <p className="text-[13px] text-slate-400 mt-1 mb-4">
                {searchQuery ? 'Try adjusting your search' : 'Create your first sales order to get started'}
              </p>
              {!searchQuery && (
                <button
                  onClick={handleAddOrder}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-[13px] font-medium"
                >
                  Create Sales Order
                </button>
              )}
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Single table -- thead is sticky so it stays pinned while tbody scrolls */}
              <div className="flex-1 overflow-y-auto">
                <table className="tl-table" style={{ tableLayout: 'fixed', width: '100%' }}>
                  <thead className="sticky top-0 z-10 bg-white">
                  <tr>
                    <th className="tl-th tl-th-teal" style={{ width: '10%' }}>
                      <div className="flex items-center gap-1">
                        Order #
                        <ColumnFilter
                          type="text"
                          value={columnFilters.order_number}
                          onChange={(val) => setColumnFilters({...columnFilters, order_number: val})}
                          placeholder="Filter order..."
                        />
                      </div>
                    </th>
                    <th className="tl-th tl-th-teal" style={{ width: isOwner ? '15%' : '18%' }}>
                      <div className="flex items-center gap-1">
                        Customer
                        <ColumnFilter
                          type="text"
                          value={columnFilters.customer_name}
                          onChange={(val) => setColumnFilters({...columnFilters, customer_name: val})}
                          placeholder="Filter customer..."
                        />
                      </div>
                    </th>
                    <th className="tl-th tl-th-teal" style={{ width: '11%' }}>
                      <div className="flex items-center gap-1">
                        Order Date
                        <ColumnFilter
                          type="date"
                          value={columnFilters.order_date}
                          onChange={(val) => setColumnFilters({...columnFilters, order_date: val})}
                        />
                      </div>
                    </th>
                    <th className="tl-th tl-th-teal" style={{ width: '11%' }}>
                      <div className="flex items-center gap-1">
                        Shipment Date
                        <ColumnFilter
                          type="date"
                          value={columnFilters.shipment_date}
                          onChange={(val) => setColumnFilters({...columnFilters, shipment_date: val})}
                        />
                      </div>
                    </th>
                    <th className="tl-th tl-th-teal" style={{ width: '10%' }}>
                      <div className="flex items-center gap-1">
                        Status
                        <ColumnFilter
                          type="status"
                          value={columnFilters.status}
                          onChange={(val) => setColumnFilters({...columnFilters, status: val})}
                          options={[
                            { value: 'DRAFT', label: 'Draft' },
                            { value: 'CONFIRMED', label: 'Confirmed' },
                            { value: 'CLOSED', label: 'Closed' },
                            { value: 'CANCELLED', label: 'Cancelled' }
                          ]}
                        />
                      </div>
                    </th>
                    {/* Staff Member Column - Owner Only */}
                    {isOwner && (
                      <th className="tl-th tl-th-teal" style={{ width: '12%' }}>
                        <div className="flex items-center gap-1">
                          <User className="w-3.5 h-3.5" />
                          Staff
                          <ColumnFilter
                            type="text"
                            value={columnFilters.staff_member}
                            onChange={(val) => setColumnFilters({...columnFilters, staff_member: val})}
                            placeholder="Filter staff..."
                          />
                        </div>
                      </th>
                    )}
                    <th className="tl-th tl-th-teal tl-th-right" style={{ width: '13%' }}>
                      <div className="flex items-center justify-end gap-1">
                        Amount
                        <ColumnFilter
                          type="number"
                          value={columnFilters.amount}
                          onChange={(val) => setColumnFilters({...columnFilters, amount: val})}
                        />
                      </div>
                    </th>
                    <th className="tl-th tl-th-teal" style={{ width: '60px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody className="animate-stagger">
                  {filteredOrders.map((order, index) => {
                    const statusBadge = getStatusBadge(order);
                    return (
                      <tr
                        key={order.id}
                        className={`tl-tr tl-tr-clickable hover:bg-teal-50/40 animate-tableRowFadeIn ${index % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                        onClick={() => handleViewOrder(order)}
                        style={{ animationDelay: `${Math.min(index * 30, 300)}ms` }}
                      >
                        <td className="tl-td" style={{ width: '10%' }}>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-[12px] font-medium text-teal-700">
                                {order.order_number?.slice(-2) || 'SO'}
                              </span>
                            </div>
                            <span className="tl-cell-primary">{order.order_number}</span>
                          </div>
                        </td>
                        <td className="tl-td tl-cell-regular" style={{ width: isOwner ? '15%' : '18%' }}>
                          {order.customer_name}
                        </td>
                        <td className="tl-td tl-cell-regular" style={{ width: '11%' }}>
                          {order.order_date ? new Date(order.order_date).toLocaleDateString('en-IN') : '--'}
                        </td>
                        <td className="tl-td tl-cell-regular" style={{ width: '11%' }}>
                          {order.expected_shipment_date ? new Date(order.expected_shipment_date).toLocaleDateString('en-IN') : '--'}
                        </td>
                        <td className="tl-td" style={{ width: '10%' }}>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${statusBadge.bg} ${statusBadge.text}`}>
                            {statusBadge.label}
                          </span>
                        </td>
                        {/* Staff Member Column - Owner Only */}
                        {isOwner && (
                          <td className="tl-td" style={{ width: '12%' }}>
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                                <User className="w-3 h-3 text-slate-500" />
                              </div>
                              <span className="text-[12px] text-slate-600 truncate">
                                {order.created_by_staff_name || 'Owner'}
                              </span>
                            </div>
                          </td>
                        )}
                        <td className="tl-td tl-td-right tl-cell-regular" style={{ width: '13%' }}>
                          ₹{(order.grand_total || 0).toLocaleString('en-IN')}
                        </td>
                        <td className="tl-td" style={{ width: '60px' }} onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
                                title="Actions"
                              >
                                <MoreVertical className="w-4 h-4" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem onClick={() => handleViewOrder(order)}>
                                <Eye className="w-4 h-4 mr-2 text-slate-500" />
                                View
                              </DropdownMenuItem>
                              {order.status === 'DRAFT' && (
                                <DropdownMenuItem onClick={(e) => handleConfirmOrder(order.id, e)}>
                                  <CheckCircle className="w-4 h-4 mr-2 text-emerald-600" />
                                  Confirm Order
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={(e) => handleDownloadPdf(order.id, e)}>
                                <Download className="w-4 h-4 mr-2 text-slate-500" />
                                Download PDF
                              </DropdownMenuItem>
                              {/* Destructive actions separated -- hide Cancel once the order
                                  has been converted to an invoice, since cancelling then is
                                  semantically incoherent (the invoice already exists). */}
                              {order.status === 'DRAFT' && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={(e) => handleDeleteOrder(order.id, e)}
                                    className="text-red-600 focus:text-red-600 focus:bg-red-50"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </>
                              )}
                              {order.status === 'CONFIRMED' && !order.converted_to_invoice && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={(e) => handleCancelOrder(order.id, e)}
                                    className="text-red-600 focus:text-red-600 focus:bg-red-50"
                                  >
                                    <XCircle className="w-4 h-4 mr-2" />
                                    Cancel Order
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>

            {/* TABLE FOOTER */}
            <div className="flex-shrink-0 px-4 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
              <p>
                Showing <span className="font-medium text-slate-700">{filteredOrders.length}</span> orders
              </p>
              <p>
                Total Value: <span className="font-medium text-slate-700">{formatCompactCurrency(stats.total_value)}</span>
              </p>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        icon={confirmModal?.icon}
        iconClass={confirmModal?.iconClass}
        iconBgClass={confirmModal?.iconBgClass}
        onConfirm={confirmModal?.onConfirm}
        onClose={() => setConfirmModal(null)}
      />
    </>
  );
};

export default SalesOrdersPage;
